import { getAuth } from 'firebase/auth';
import type { InsightNotification } from '../notificationTypes';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

interface Envelope<T> {
  apiVersion: string;
  asOf: string;
  data: T;
  warnings: Array<{ code: string; message: string }>;
}

async function handleResponse<T>(response: Response): Promise<Envelope<T>> {
  const text = await response.text().catch(() => '');
  let payload: Envelope<T> | { detail?: string; title?: string } | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON response from notifications API: HTTP ${response.status}`);
    }
  }

  if (!response.ok) {
    let message: string | undefined;
    if (payload && 'detail' in payload) message = payload.detail;
    else if (payload && 'title' in payload) message = payload.title;
    throw new Error(message || `HTTP ${response.status}`);
  }

  return payload as Envelope<T>;
}

export class InsightNotificationsApiClient {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  private url(projectId: string, suffix = ''): string {
    return `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/notifications${suffix}`;
  }

  async list(
    projectId: string,
    options: { status?: 'unread' | 'read' | 'dismissed'; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<InsightNotification[]> {
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    const response = await fetch(this.url(projectId, query ? `?${query}` : ''), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<{ notifications: InsightNotification[] }>(response);
    return payload.data.notifications;
  }

  async markRead(projectId: string, notificationId: string): Promise<void> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(notificationId)}/read`), {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    await handleResponse<{ updated: boolean }>(response);
  }

  async markAllRead(projectId: string): Promise<void> {
    const response = await fetch(this.url(projectId, '/read-all'), {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    await handleResponse<{ updated: number }>(response);
  }
}
